import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button, { ButtonColor, ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Loader } from 'src/components/atoms/Loader';
import Text, { TextAlign, TextVariant } from 'src/components/atoms/Text';
import Modal, { ModalVariant } from 'src/components/organisms/Modal';
import Tag, { TagColor } from 'src/components/atoms/Tag';
import {
  AssetVerificationReviewProvider,
  buildCompletePayload,
  dedupeByCode,
  proofForDocument,
  reasonCodeLabel,
  summarizeDocument,
  unionReasonCodes,
  voaStorageKey,
  VoaReasonCode,
  useCompleteAssetReview,
  useHydrateReviewState,
  useRecalculateAssets,
  validateReviewForSubmit
} from 'src/features/assetVerification';
import { useProofsFilteredByEntryId } from 'src/features/proofs/hooks';
import useUserFeature from 'src/hooks/useUserFeature';
import { IProof } from 'src/ts/interfaces';
import { currencyFormat } from 'src/helpers/currencyFormat';
import {
  DocIdTooltipContent,
  DocIdTooltipWrapper,
  DocPreviewEmpty,
  DocPreviewImage,
  DocMetaCell,
  DocShortId,
  DocumentEditButton,
  DocumentList,
  DocumentReasonList,
  DocumentRow,
  FooterBar,
  SummaryLayout,
  TotalRow,
  ViolationList
} from './styles';

type ContentProps = {
  entryId: string;
  reviewItemId: string;
  proofs: IProof[];
  proofsReady: boolean;
};

const AssetCalculationContent = ({ entryId, reviewItemId, proofs, proofsReady }: ContentProps) => {
  const { state, isLoading, isError, storageReset, retry } = useHydrateReviewState(
    entryId,
    proofs,
    proofsReady
  );
  const navigate = useNavigate();
  const storageKey = voaStorageKey(entryId);
  const { onCompleteReview } = useCompleteAssetReview(storageKey);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const violations = useMemo(() => validateReviewForSubmit(state), [state]);

  // Reason codes per document (FVS-attached plus analyst rejections), deduped by
  // code, so the analyst sees what is excluded before submitting. Nothing fetched:
  // the codes are already in review state.
  const codesByDocument = useMemo(() => {
    const map = new Map<string, VoaReasonCode[]>();
    state.accounts.forEach((account) =>
      account.statements.forEach((statement) => {
        const existing = map.get(statement.documentId) ?? [];
        map.set(statement.documentId, dedupeByCode([...existing, ...unionReasonCodes(statement)]));
      })
    );
    return map;
  }, [state.accounts]);

  // FVS computes the totals (contract §6, 2026-08-03 design change) — the FE renders
  // its recalculate preview and never does asset math. A preview failure does not
  // block submit: complete recomputes authoritatively server-side.
  const recalculation = useRecalculateAssets(
    entryId,
    state,
    state.hydratedFrom !== null && violations.length === 0
  );

  if (isLoading) return <Loader />;
  // Zero documents is permanent, same rule as the flow page: nothing to summarise,
  // nothing to submit, and no retry that could change it.
  if (!isError && state.documents.length === 0) {
    return (
      <FooterBar>
        <Text align={TextAlign.center} data-testid="asset-calculation-no-documents">
          This entry has no asset documents to review.
        </Text>
      </FooterBar>
    );
  }
  if (isError) {
    return (
      <FooterBar>
        <Text align={TextAlign.center}>The review could not be loaded.</Text>
        <Button
          name="asset-calculation-retry"
          data-testid="asset-calculation-retry"
          size={ButtonSize.normal}
          color={ButtonColor.primary}
          variant={ButtonVariant.contained}
          onClick={() => retry()}
        >
          Retry
        </Button>
      </FooterBar>
    );
  }

  // Same route the flow page mounts on; ?doc= selects the document to edit.
  const flowDocRoute = (documentIndex: number) =>
    `/my-work/asset-verification/${reviewItemId}/${entryId}?doc=${documentIndex}`;

  const onConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onCompleteReview(entryId, buildCompletePayload(state), {
        entry_id: entryId,
        applicant_id: state.applicantId,
        asset_review_item_id: reviewItemId
      });
    } finally {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <SummaryLayout>
      <Text isBold={true} variant={TextVariant.h2} align={TextAlign.left}>
        Asset calculation
      </Text>
      <Text align={TextAlign.left}>{state.applicantName}</Text>
      {storageReset && (
        <div data-testid="voa-storage-reset-notice" role="status">
          <Text align={TextAlign.left}>
            An app update reset this review. Anything you had already entered needs to be entered
            again.
          </Text>
        </div>
      )}
      <DocumentList>
        {state.documents.map((doc) => {
          const summary = summarizeDocument(state.accounts, doc.documentId);
          const proof = proofForDocument(proofs, doc.documentId);
          const shortId = proof?.short_id ?? `Document ${doc.index + 1}`;
          return (
            <DocumentRow key={doc.documentId} data-testid={`document-row-${doc.index}`}>
              <DocIdTooltipWrapper>
                <DocShortId
                  tabIndex={0}
                  aria-describedby={`document-id-tooltip-${doc.index}`}
                  data-testid={`document-short-id-${doc.index}`}
                >
                  {shortId}
                </DocShortId>
                <DocIdTooltipContent
                  id={`document-id-tooltip-${doc.index}`}
                  role="tooltip"
                  data-testid={`document-id-tooltip-${doc.index}`}
                >
                  {proof?.thumb ? (
                    <DocPreviewImage src={proof.thumb} alt={`Preview of ${shortId}`} />
                  ) : (
                    <DocPreviewEmpty>No thumbnail available</DocPreviewEmpty>
                  )}
                </DocIdTooltipContent>
              </DocIdTooltipWrapper>
              <DocMetaCell data-testid={`document-meta-${doc.index}`}>
                {summary.isRejected ? (
                  <Tag label="Rejected" color={TagColor.red} showBackground />
                ) : (
                  <>
                    {summary.institutions}
                    {summary.isStaleFlagged && (
                      <Tag label="90+ days" color={TagColor.yellow} showBackground />
                    )}
                    {summary.isOverridden && (
                      <Tag label="Overridden" color={TagColor.gray} showBackground />
                    )}
                  </>
                )}
              </DocMetaCell>
              <DocumentEditButton
                type="button"
                data-testid={`document-edit-${doc.index}`}
                onClick={() => navigate(flowDocRoute(doc.index))}
              >
                Edit
              </DocumentEditButton>
              {(codesByDocument.get(doc.documentId) ?? []).length > 0 && (
                <DocumentReasonList data-testid={`document-reasons-${doc.index}`}>
                  {(codesByDocument.get(doc.documentId) ?? []).map((reason) => (
                    <li key={reason.code}>{`⚠ ${reasonCodeLabel(reason)}`}</li>
                  ))}
                </DocumentReasonList>
              )}
            </DocumentRow>
          );
        })}
      </DocumentList>
      <TotalRow>
        <Text isBold={true} align={TextAlign.left}>
          Total verified assets
        </Text>
        {recalculation.total !== null && !recalculation.isLoading && !recalculation.isError ? (
          <Text align={TextAlign.left} data-testid="verified-assets-total">
            {currencyFormat(recalculation.total)}
          </Text>
        ) : recalculation.hasVerifiedNothing &&
          !recalculation.isLoading &&
          !recalculation.isError ? (
          // FVS computed a total and it is "none" — distinct from $0.00 and from
          // the not-yet-calculated fallback. Submit stays enabled: all-excluded
          // is a legitimate outcome.
          <Text align={TextAlign.left} data-testid="verified-assets-total-none">
            No verified assets
          </Text>
        ) : (
          <Text align={TextAlign.left} data-testid="verified-assets-total-pending">
            {recalculation.isLoading ? 'Calculating…' : 'Calculated on submit'}
          </Text>
        )}
        {recalculation.isError && (
          <Button
            name="verified-assets-total-retry"
            data-testid="verified-assets-total-retry"
            size={ButtonSize.small}
            variant={ButtonVariant.ghost}
            onClick={() => recalculation.retry()}
          >
            Retry
          </Button>
        )}
      </TotalRow>
      {violations.length > 0 && (
        <ViolationList data-testid="submit-violations">
          {violations.map((violation, index) => (
            <li key={`${violation.code}-${violation.accountKey}-${index}`}>
              <button
                type="button"
                data-testid={`violation-link-${index}`}
                onClick={() => navigate(flowDocRoute(Math.max(violation.documentIndex, 0)))}
              >
                Document {Math.max(violation.documentIndex, 0) + 1}: {violation.message}
              </button>
            </li>
          ))}
        </ViolationList>
      )}
      <FooterBar>
        <Button
          name="asset-calculation-submit"
          data-testid="asset-calculation-submit"
          size={ButtonSize.normal}
          color={ButtonColor.primary}
          variant={ButtonVariant.contained}
          // An unhydrated/empty review yields zero violations by coincidence — the
          // submit gate must not depend on that.
          isDisabled={
            state.hydratedFrom === null ||
            state.documents.length === 0 ||
            violations.length > 0 ||
            isSubmitting
          }
          onClick={() => setShowConfirm(true)}
        >
          Submit Review
        </Button>
        <Button
          name="asset-calculation-previous"
          data-testid="asset-calculation-previous"
          size={ButtonSize.normal}
          variant={ButtonVariant.outline}
          onClick={() => navigate(flowDocRoute(Math.max(state.documents.length - 1, 0)))}
        >
          Previous
        </Button>
      </FooterBar>
      <Modal
        title="Submit this asset review?"
        showModal={showConfirm}
        variant={ModalVariant.none}
        labelButtonConfirm="Submit Review"
        labelButtonCancel="Cancel"
        isLoadingButtonConfirm={isSubmitting}
        isDisabledButtonCancel={isSubmitting}
        onConfirm={onConfirmSubmit}
        onCancel={() => setShowConfirm(false)}
        data-testid="submit-review-confirm-modal"
      >
        <Text align={TextAlign.left}>
          {recalculation.total !== null && !recalculation.isError
            ? `This submits the corrected accounts — ${currencyFormat(
                recalculation.total
              )} in total verified assets — and completes the review. This can't be undone.`
            : recalculation.hasVerifiedNothing && !recalculation.isError
            ? "This submits the corrected accounts — no verified assets — and completes the review. This can't be undone."
            : "This submits the corrected accounts and completes the review — FVS calculates the total verified assets. This can't be undone."}
        </Text>
      </Modal>
    </SummaryLayout>
  );
};

const AssetCalculationPage = () => {
  const { entryId, reviewItemId } = useParams();
  const hasReviewFeature = useUserFeature('asset_verification_review');

  const {
    proofs,
    isUninitialized: isProofsUninitialized,
    isLoading: isLoadingProofs,
    isError: isProofsError
  } = useProofsFilteredByEntryId(entryId ?? '');
  const proofsReady = !isProofsUninitialized && !isLoadingProofs;

  if (!entryId || !reviewItemId) return <Loader />;
  if (!hasReviewFeature) {
    return (
      <Text align={TextAlign.center} data-testid="asset-verification-denied">
        You do not have access to asset verification reviews.
      </Text>
    );
  }
  // Mounting does not wait on the proofs fetch; see useHydrateReviewState.
  if (isProofsError) {
    return (
      <Text align={TextAlign.center} data-testid="asset-verification-proofs-error">
        The applicant&apos;s documents could not be loaded. Refresh and try again.
      </Text>
    );
  }

  return (
    // key: same remount-on-navigate guard as AssetVerificationFlowPage — without it
    // the previous entry's hydrated state survives a client-side entry switch.
    <AssetVerificationReviewProvider
      key={voaStorageKey(entryId)}
      storageKey={voaStorageKey(entryId)}
    >
      <AssetCalculationContent
        entryId={entryId}
        reviewItemId={reviewItemId}
        proofs={proofs}
        proofsReady={proofsReady}
      />
    </AssetVerificationReviewProvider>
  );
};

export default AssetCalculationPage;
